import { useQuery } from 'react-query';
import {
	getUserCompletedMeetingsCount,
	checkUserEligibility,
	getJapEnrollment,
	getPhoneVerificationStatus,
	getUserById,
	getUserProfileScores,
	verifyCookie,
	verifyJwt,
	verifyPracticeJwt,
	getUniquePhoneNumber,
	getUniqueEmail,
	getUsersToBeScreened,
	getMyBDPocs,
	getAllAccountManagers,
	getAllPrepCoaches,
	getAllUserByUniversity,
	getUniversityStudentStats,
	getApprovedBehaviouralFlags,
} from 'src/apis/user';
import { isRecruiterSide } from 'src/config/env.config';
import {
	FilterUniversityStudentsInput,
	GetCompletedMeetingCountAPIResponse,
	GetJwtToken,
	GetMyBDPocsAPIResponse,
	GetProfileScoreAPIResponse,
	GetUniqueEmailAPIResponse,
	GetUniquePhoneNumberAPIResponse,
	GetUniversityStudentStatsAPIResponse,
	GetUserByIdAPIResponse,
	GetUserEligibilityAPIResponse,
	GetUserVerificationAPIResponse,
	GetUsersAPIResponse,
	GetUsersForUniversityAPIResponse,
	GetUsersToBeScreenedAPIResponse,
	JapEnrollmentByIdAPIResponse,
	ScreeningUsersFilterQueryInput,
	UserFilterQuery,
} from 'types';

export const useGetUserById = (id?: string, onComplete?: (data: GetUserByIdAPIResponse) => void) => {
	return useQuery({
		queryKey: ['getUserById', id],
		queryFn: async () => (await getUserById(id ?? '')) as GetUserByIdAPIResponse,
		staleTime: 0,
		enabled: !!id,
		refetchOnMount: true,
		onSuccess(data) {
			onComplete && onComplete(data);
		},
	});
};

export const useVerifyCookie = (
	{
		jwtToken,
		refetchOnMount = true,
		jwtInternalStudentLinkToken,
	}: { jwtToken?: string; jwtInternalStudentLinkToken?: string; refetchOnMount?: boolean },
	onComplete: (data: GetUserByIdAPIResponse) => void,
	onError?: (error: unknown) => void,
) => {
	return useQuery({
		queryKey: ['verifyCookie', jwtToken || jwtInternalStudentLinkToken || 'session'],
		queryFn: async () => (await verifyCookie({ jwtToken, jwtInternalStudentLinkToken })) as GetUserByIdAPIResponse,
		staleTime: 0,
		refetchOnMount: refetchOnMount,
		onSuccess(data) {
			onComplete(data);
		},
		onError(error) {
			onError?.(error);
		},
	});
};

export const useGetPhoneVerificationStatus = (emailOrPhone: string) => {
	return useQuery({
		queryKey: ['getPhoneVerificationStatus', emailOrPhone],
		queryFn: async () => (await getPhoneVerificationStatus(emailOrPhone)) as GetUserVerificationAPIResponse,
		staleTime: 0,
		enabled: !!emailOrPhone,
		refetchOnMount: true,
	});
};

export const useGetJwtTokenQuery = (
	token: string,
	onComplete: (data: GetJwtToken) => void,
	handleError: () => void,
) => {
	return useQuery({
		queryKey: ['verifyJwt', token],
		queryFn: async () => (await verifyJwt({ token })) as GetJwtToken,
		staleTime: 0,
		enabled: !!token,
		refetchOnMount: true,
		onSuccess(data) {
			onComplete(data);
		},
		onError() {
			handleError();
		},
	});
};

export const useGetJwtTokenForPracticeQuery = (
	args: { questionId: string; token: string },
	onComplete: (data: GetJwtToken) => void,
	handleError: () => void,
) => {
	return useQuery({
		queryKey: ['verifyPracticeJwt'],
		queryFn: async () => (await verifyPracticeJwt(args)) as GetJwtToken,
		staleTime: 0,
		enabled: !!args.token && !!args.questionId,
		refetchOnMount: true,
		onSuccess(data) {
			onComplete(data);
		},
		onError() {
			handleError();
		},
	});
};

export const useGetCompletedMeetingCount = (isJobseekerLoggedIn: boolean, idOrHandler: string) => {
	return useQuery({
		queryKey: ['getCompletedMeetingsCount', idOrHandler],
		queryFn: async () => (await getUserCompletedMeetingsCount(idOrHandler)) as GetCompletedMeetingCountAPIResponse,
		staleTime: 0,
		enabled: !!idOrHandler && isJobseekerLoggedIn,
		refetchOnMount: true,
	});
};
export const useGetUserEligibilityQuery = () => {
	return useQuery({
		queryKey: ['checkUserEligibility'],
		queryFn: async () => (await checkUserEligibility()) as GetUserEligibilityAPIResponse,
		staleTime: 0,
		refetchOnMount: true,
	});
};
export const useGetUserProfileScores = (handler: string) => {
	return useQuery({
		queryKey: ['getUserProfileScores', handler],
		queryFn: async () => (await getUserProfileScores(handler)) as GetProfileScoreAPIResponse,
		enabled: !!handler,
		staleTime: 0,
		refetchOnMount: true,
	});
};

export const useGetJapEnrollment = (userId: string, onComplete?: (data: JapEnrollmentByIdAPIResponse) => void) => {
	return useQuery({
		queryKey: ['getJapEnrollment'],
		queryFn: async () => (await getJapEnrollment()) as JapEnrollmentByIdAPIResponse,
		staleTime: 0,
		enabled: !!userId,
		refetchOnMount: true,
		onSuccess(data) {
			onComplete && onComplete(data);
		},
	});
};

export const useGetUniquePhoneNumber = (
	phoneNumber: string,
	userId: string,
	onComplete?: (data: GetUniquePhoneNumberAPIResponse) => void,
) => {
	return useQuery({
		queryKey: ['getUniquePhoneNumber', phoneNumber],
		queryFn: async () => (await getUniquePhoneNumber(phoneNumber, userId)) as GetUniquePhoneNumberAPIResponse,
		enabled: !!phoneNumber && !!userId && !isRecruiterSide,
		staleTime: 0,
		refetchOnMount: true,
		onSettled(data) {
			if (data) onComplete && onComplete(data);
		},
	});
};

export const useGetUniqueEmail = (
	email: string,
	userId: string,
	onComplete?: (data: GetUniqueEmailAPIResponse) => void,
) => {
	return useQuery({
		queryKey: ['getUniqueEmail', email, userId],
		queryFn: async () => (await getUniqueEmail(email, userId)) as GetUniqueEmailAPIResponse,
		enabled: !!email && !!userId && isRecruiterSide,
		staleTime: 0,
		refetchOnMount: true,
		onSettled(data) {
			if (data) onComplete && onComplete(data);
		},
	});
};

export const useGetUsersToBeScreened = (
	filterData: ScreeningUsersFilterQueryInput,
	mentorId: string,
	onComplete?: (data: GetUsersToBeScreenedAPIResponse) => void,
) => {
	return useQuery({
		queryKey: ['getUsersToBeScreened', filterData, mentorId],
		queryFn: async () => (await getUsersToBeScreened(filterData, mentorId)) as GetUsersToBeScreenedAPIResponse,
		enabled: !!mentorId,
		staleTime: 0,
		refetchOnMount: true,
		onSettled(data) {
			if (data) onComplete && onComplete(data);
		},
	});
};

export const useGetMyBDPocsQuery = (onComplete?: (data: GetMyBDPocsAPIResponse) => void) => {
	return useQuery({
		queryKey: ['getMyBDPocs'],
		queryFn: async () => (await getMyBDPocs()) as GetMyBDPocsAPIResponse,
		staleTime: 0,
		refetchOnMount: true,
		onSettled(data) {
			if (data) onComplete && onComplete(data);
		},
	});
};

export const useGetAllAccountManagers = (
	filterQuery?: UserFilterQuery,
	onComplete?: (data: GetUsersAPIResponse) => void,
) => {
	return useQuery({
		queryKey: ['getAllAccountManagers', filterQuery],
		queryFn: async () => await getAllAccountManagers({ filterQuery }),
		staleTime: 0,
		refetchOnMount: true,
		onSuccess(data) {
			onComplete && onComplete(data);
		},
	});
};

export const useGetAllPrepCoaches = (onComplete?: (data: GetUsersAPIResponse) => void) => {
	return useQuery({
		queryKey: ['getAllPrepCoaches'],
		queryFn: async () => await getAllPrepCoaches(),
		staleTime: 0,
		refetchOnMount: true,
		onSuccess(data) {
			onComplete && onComplete(data);
		},
	});
};

export const useGetAllUserByUniversity = (
	args: FilterUniversityStudentsInput,
	onComplete?: (data: GetUsersForUniversityAPIResponse) => void,
) => {
	return useQuery({
		queryKey: ['getAllUserByUniversity', ...Object.values(args)],
		queryFn: async () => await getAllUserByUniversity(args),
		staleTime: 0,
		refetchOnMount: true,
		onSuccess(data) {
			onComplete && onComplete(data);
		},
	});
};

export const useGetUniversityStudentStats = (onComplete?: (data: GetUniversityStudentStatsAPIResponse) => void) => {
	return useQuery({
		queryKey: ['getUniversityStudentStats'],
		queryFn: async () => await getUniversityStudentStats(),
		staleTime: 0,
		refetchOnMount: true,
		onSuccess(data) {
			onComplete && onComplete(data);
		},
	});
};

export const useGetApprovedBehaviouralFlags = (userId?: string) => {
	return useQuery({
		queryKey: ['getApprovedBehaviouralFlags', userId],
		queryFn: async () => await getApprovedBehaviouralFlags(userId ?? ''),
		enabled: !!userId,
		staleTime: 0,
		refetchOnMount: true,
	});
};
